import { useAppSelector } from "@/redux/hooks";
import { useNavigationUtils } from "@/utils/navigationHooks";
import { usePrivy } from "@privy-io/react-auth";
import { useEffect, useState } from "react";
import { getCookie } from "typescript-cookie";
import TransactionSnackbar from "../TransactionSnackbar";

interface AutoLogoutProps {
  children: React.ReactNode;
}

async function checkAndHandleTokens(
  getAccessToken: () => Promise<string | null>,
  handleLogout: () => void
) {
  try {
    // Check cookie token
    const cookieToken = getCookie("accessToken");
    // Check Privy token
    const privyToken = await getAccessToken();

    if (!cookieToken || !privyToken) {
      await handleLogout();
      return false;
    }
    return true;
  } catch (error) {
    console.error("Token check error:", error);
    await handleLogout();
    return false;
  }
}

export default function AutoLogout({ children }: AutoLogoutProps) {
  const { ready, authenticated, getAccessToken } = usePrivy();
  const { handleLogout } = useNavigationUtils();
  const isVerified = useAppSelector((state) => state.verify.isVerified);
  const [showSnackbar, setShowSnackbar] = useState(false);

  useEffect(() => {
    if (ready && authenticated && isVerified) {
      const checkTokens = async () => {
        const hasTokens = await checkAndHandleTokens(getAccessToken, () => {
          setShowSnackbar(true);
          handleLogout();
        });
        if (!hasTokens) {
          setShowSnackbar(true);
        }
      };

      // Check immediately
      checkTokens();

      // Set up interval to check periodically
      const intervalId = setInterval(checkTokens, 5 * 60 * 1000);

      // Clean up interval on unmount
      return () => clearInterval(intervalId);
    }
  }, [ready, authenticated, isVerified, getAccessToken, handleLogout]);

  return (
    <>
      {children}
      <TransactionSnackbar
        open={showSnackbar}
        onClose={() => setShowSnackbar(false)}
        status="error"
        title="Session Expired"
        message="Your session has expired. Please log in again."
      />
    </>
  );
}
